import { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { updateQuotationClient } from "../../../../../actions/admin/quotation";
import AdminModal from "../../../components/AdminModal";
import AdminInput from "../../../components/AdminInput";
import AdminPhoneNumberInput from "../../../components/AdminPhoneNumberInput";
import AdminPostalCodeInput from "../../../components/AdminPostalCodeInput";
import { POSTAL_CODE_STATUS } from "../../../../input/postal-code";

const PRONOUNCE = [
    { label: "-", value: "-" },
    { label: "Mr.", value: "Mr." },
    { label: "Ms.", value: "Ms." },
    { label: "Mrs.", value: "Mrs." },
    { label: "Mr./Ms.", value: "Mr./Ms." },
];

const customStylesSelect = {
    control: (provided) => ({
        ...provided,
        minHeight: "48px",
        height: "38px",
        marginTop: "10px",
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: "38px",
        padding: "0 8px",
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: "38px",
    }),
};

const stripParentheses = (value) =>
    value ? value.replace(/[()]/g, "").trim() : "";

const getInitialForm = (client) => {
    if (!client) return {
        honorific: "-",
        name: "",
        email: "",
        prefixNumber: "+81",
        phoneNumber: "",
        country: "Japan",
        countryCode: "JP",
        postalCode: "",
        billingAddress: "",
        city: "",
    };

    const detail =
        client.role === "individual"
            ? client.individualDetail
            : client.businessDetail;
    const billing = detail?.billingInformation || {};

    return {
        honorific: billing.pronounced || "-",
        name: billing.name || client.name || "",
        email: client.email || "",
        prefixNumber: stripParentheses(billing.prefix_number) || "+81",
        phoneNumber: billing.phone || "",
        country: billing.country || "Japan",
        countryCode: billing.country_code || "JP",
        postalCode: billing.postal_code === "-" ? "" : (billing.postal_code || ""),
        billingAddress: billing.address || "",
        city: billing.city || "",
    };
};

// Errors stay inside the modal (instead of closing it) so the admin never
// loses what they typed because one request failed.
const EditClientModal = ({ show, client, onClose, onSuccessEditClient }) => {
    const [form, setForm] = useState(getInitialForm(client));
    const [formValidation, setFormValidation] = useState({ name: true, billingAddress: true });
    const isCountryChangeRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveErrorMessage, setSaveErrorMessage] = useState("");
    const [postalCodeStatus, setPostalCodeStatus] = useState(POSTAL_CODE_STATUS.IDLE);
    // Bumped on every open so the postal code field starts fresh (see AdminPostalCodeInput).
    const [formSessionKey, setFormSessionKey] = useState(0);

    // Re-initialise form whenever the modal opens with a (potentially different) client
    useEffect(() => {
        if (show) {
            isCountryChangeRef.current = false;
            setForm(getInitialForm(client));
            setFormValidation({ name: true, billingAddress: true });
            setSaveErrorMessage("");
            setPostalCodeStatus(POSTAL_CODE_STATUS.IDLE);
            setFormSessionKey((key) => key + 1);
            setIsSaving(false);
        }
    }, [show, client]);

    const countryWithoutPostCode = process.env.REACT_APP_COUNTRY_WITHOUT_POSTCODE || "";
    const isPostalCodeDisabled = countryWithoutPostCode.split(",").includes(form.countryCode);
    const isCityDisabled = !isPostalCodeDisabled;

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "name" || name === "billingAddress") {
            const hasInvalidChars = value
                .split("")
                .some((char) => !/[\w\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(char));

            setFormValidation((prev) => ({
                ...prev,
                [name]: value === "" || !hasInvalidChars,
            }));
        }

        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCountryCodeChange = (dialCode, countryCode, name) => {
        isCountryChangeRef.current = true;
        setForm((prev) => ({
            ...prev,
            country: name,
            countryCode,
            prefixNumber: dialCode,
            postalCode: countryWithoutPostCode.split(",").includes(countryCode) ? "-" : "",
            city: "",
        }));
    };

    const handleCityFound = (city) => setForm((prev) => ({ ...prev, city }));
    const handlePostalCodeNotFound = () => setForm((prev) => ({ ...prev, city: "" }));

    // Hold Save while the postal code is being checked or does not exist.
    const isPostalCodeBlockingSave =
        postalCodeStatus === POSTAL_CODE_STATUS.CHECKING ||
        postalCodeStatus === POSTAL_CODE_STATUS.NOT_FOUND;

    const handleSubmit = async () => {
        if (isSaving || isPostalCodeBlockingSave || !client?._id) return;

        const allValid = Object.values(formValidation).every(Boolean);
        if (!allValid) return;

        setIsSaving(true);
        setSaveErrorMessage("");
        try {
            const updatedClient = await updateQuotationClient(client._id, form);
            onSuccessEditClient(updatedClient);
        } catch (e) {
            setSaveErrorMessage(e.message || "Failed to update client!");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <AdminModal
            show={show}
            title="Edit Client"
            size="modal-lg"
            onClose={onClose}
            description="Kindly enter the client's name and address using the Roman alphabet only (e.g., Tanaka Taro)"
        >
            <div className="row px-2">
                <div className="col-3 m-0 p-0 pe-1">
                    <p className="mb-2">Honorifics</p>
                    <Select
                        options={PRONOUNCE}
                        value={PRONOUNCE.find((p) => p.value === form.honorific) || null}
                        onChange={(e) =>
                            handleChange({ target: { name: "honorific", value: e.value } })
                        }
                        styles={customStylesSelect}
                        name="honorific"
                    />
                </div>
                <div className="col m-0 p-0 ps-1">
                    <label className="mb-2 fs-14">Name</label>
                    <AdminInput
                        placeholder="Name"
                        value={form.name}
                        onChange={handleChange}
                        name="name"
                        isInvalid={!formValidation.name}
                        invalidMessage="Kindly enter the client's name and address using the Roman alphabet only (e.g., Tanaka Taro)"
                    />
                </div>
            </div>

            <div className="row px-2">
                <div className="col-6 m-0 p-0 pe-1">
                    <label className="mb-2 fs-14">Email</label>
                    <AdminInput
                        placeholder="Email"
                        value={form.email}
                        onChange={() => { }}
                        name="email"
                        disable={true}
                    />
                </div>
                <div className="col-6 m-0 p-0 ps-1">
                    <label className="mb-2 fs-14">Phone No.</label>
                    <AdminPhoneNumberInput
                        value={form.phoneNumber}
                        onChange={handleChange}
                        countryCode={form.countryCode}
                        dialCode={form.prefixNumber}
                        onCountrySelect={handleCountryCodeChange}
                    />
                </div>
            </div>

            <div className="row px-2">
                <div className="col-4 m-0 p-0 pe-1">
                    <label className="mb-2 fs-14">Country</label>
                    <AdminInput
                        placeholder="Country"
                        value={form.country}
                        onChange={() => { }}
                        name="country"
                        disable={true}
                    />
                </div>
                <div className="col-4 m-0 p-0 px-1">
                    <label className="mb-2 fs-14">Postal Code</label>
                    <AdminPostalCodeInput
                        key={formSessionKey}
                        value={form.postalCode}
                        onChange={handleChange}
                        countryCode={form.countryCode}
                        disabled={isPostalCodeDisabled}
                        onCityFound={handleCityFound}
                        onPostalCodeNotFound={handlePostalCodeNotFound}
                        onStatusChange={setPostalCodeStatus}
                    />
                </div>
                <div className="col-4 m-0 p-0">
                    <label className="mb-2 fs-14">City</label>
                    <AdminInput
                        placeholder="City"
                        value={form.city}
                        onChange={handleChange}
                        name="city"
                        disable={isCityDisabled}
                    />
                </div>
            </div>
            <div className="row px-2">
                <div className="col-12 m-0 p-0">
                    <label className="mb-2 fs-14">Billing Address</label>
                    <AdminInput
                        placeholder="Billing Address"
                        value={form.billingAddress}
                        onChange={handleChange}
                        name="billingAddress"
                        isInvalid={!formValidation.billingAddress}
                        invalidMessage="Kindly enter the client's name and address using the Roman alphabet only (e.g., Tanaka Taro)"
                    />
                </div>
            </div>

            {saveErrorMessage && (
                <p className="tw:!mt-3 tw:!mb-0 tw:!text-[14px] tw:!text-[#EA4335]" role="alert">
                    {saveErrorMessage}
                </p>
            )}

            <div className="d-grid mt-3">
                <button
                    className="btn rounded bg-gold py-2"
                    onClick={handleSubmit}
                    disabled={isSaving || isPostalCodeBlockingSave}
                >
                    <span className="text-white">
                        {isSaving ? "Saving…" : postalCodeStatus === POSTAL_CODE_STATUS.CHECKING ? "Checking postal code…" : "Save"}
                    </span>
                </button>
            </div>
        </AdminModal>
    );
};

export default EditClientModal;
